import React, { useState, useEffect, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { motion, AnimatePresence } from "framer-motion";
import { HiSwitchHorizontal } from "react-icons/hi";
import RATES from "../config/rates";
import { springBouncy, tapShrink } from "../utils/animations";

const CURRENCIES = ["USD", "EUR", "IQD"];

function convert(amount, from, to) {
  if (from === to) return amount;
  const rate = RATES[`${from}_${to}`];
  return rate ? amount * rate : 0;
}
function getRate(from, to) {
  return from === to ? 1 : RATES[`${from}_${to}`] || 0;
}
function getTargetCurrency(from) {
  return from === "IQD" ? "USD" : "IQD";
}

/* Animated digits — each digit enters individually */
function AnimatedResult({ value }) {
  const chars = value.split("");
  return (
    <span className="inline-flex overflow-hidden">
      {chars.map((ch, i) => (
        <motion.span
          key={`${i}-${ch}`}
          initial={{ opacity: 0, y: 20, filter: "blur(4px)" }}
          animate={{ opacity: 1, y: 0, filter: "blur(0px)" }}
          transition={{ ...springBouncy, delay: i * 0.03 }}
          className="inline-block"
        >
          {ch}
        </motion.span>
      ))}
    </span>
  );
}

export default function CurrencyConverter() {
  const { t } = useTranslation();
  const [amount, setAmount] = useState("1");
  const [from, setFrom] = useState("USD");
  const [to, setTo] = useState("IQD");
  const [focused, setFocused] = useState(false);
  const [swapKey, setSwapKey] = useState(0);

  useEffect(() => { setTo(getTargetCurrency(from)); }, [from]);

  const result = useCallback(() => {
    const num = parseFloat(amount);
    if (isNaN(num) || num <= 0) return "0";
    return convert(num, from, to).toLocaleString(undefined, { maximumFractionDigits: 2 });
  }, [amount, from, to]);

  const handleSwap = () => {
    const prev = from;
    setFrom(to);
    setTo(prev);
    setSwapKey((k) => k + 1);
  };

  const rateDisplay = `1 ${from} = ${getRate(from, to).toLocaleString()} ${to}`;
  const resultLabel = to === "IQD" ? t("converter.resultLabel") : to === "USD" ? t("converter.resultLabelUSD") : t("converter.resultLabelEUR");

  return (
    <motion.div
      whileHover={{ y: -4 }}
      whileTap={tapShrink}
      transition={{ type: "spring", stiffness: 200, damping: 20 }}
      className={`group relative bg-white/95 dark:bg-white/[0.07] backdrop-blur-xl border rounded-3xl shadow-card dark:shadow-card-dark overflow-hidden transition-all duration-500 gradient-border ${
        focused
          ? "border-highlight/40 shadow-[0_0_30px_rgba(201,162,39,0.12)] dark:shadow-[0_0_30px_rgba(201,162,39,0.08)]"
          : "border-gray-200 dark:border-white/10 hover:border-highlight/30 dark:hover:border-highlight/30 hover:shadow-card-hover dark:hover:shadow-card-dark-hover"
      }`}
    >
      {/* Shimmer overlay on hover */}
      <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-700 animate-shimmer rounded-3xl pointer-events-none" />

      {/* Header */}
      <div className="relative flex items-center justify-center gap-3 px-6 pt-6 pb-4">
        <span className="text-lg md:text-xl font-bold text-main dark:text-white font-cairo">
          {t("converter.title")}
        </span>
        <motion.div
          animate={{ rotate: 360 }}
          transition={{ duration: 8, repeat: Infinity, ease: "linear" }}
          className="w-9 h-9 bg-highlight/10 border border-highlight/20 rounded-full flex items-center justify-center"
        >
          <HiSwitchHorizontal className="text-highlight text-lg" />
        </motion.div>
      </div>

      {/* Input row */}
      <div className="relative px-6 pb-4">
        <label className="block text-gray-400 dark:text-white/40 text-xs font-cairo mb-1.5 text-end">
          {t("converter.amount")}
        </label>
        <motion.div
          animate={focused ? { scale: 1.01 } : { scale: 1 }}
          transition={{ type: "spring", stiffness: 300, damping: 25 }}
          className={`flex items-stretch rounded-xl overflow-hidden shadow-sm border transition-all duration-300 ${
            focused
              ? "border-highlight/40 shadow-[0_0_16px_rgba(201,162,39,0.1)] bg-white dark:bg-white/10"
              : "border-gray-200 dark:border-white/10 bg-gray-50 dark:bg-white/5"
          }`}
        >
          <div className="flex items-center gap-1.5 bg-gray-100 dark:bg-white/10 border-e border-gray-200 dark:border-white/10 px-4">
            <select
              value={from}
              onChange={(e) => setFrom(e.target.value)}
              className="bg-transparent text-main dark:text-white font-inter font-bold text-sm focus:outline-none cursor-pointer py-3 appearance-none pe-1"
            >
              {CURRENCIES.map((c) => (
                <option key={c} value={c} className="bg-white dark:bg-main text-main dark:text-white">{t(`converter.${c}`)}</option>
              ))}
            </select>
            <span className="text-gray-400 dark:text-white/40 text-xs font-inter">
              {t(`converter.${from}_label`)}
            </span>
          </div>
          <input
            type="number"
            min="0"
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
            onFocus={() => setFocused(true)}
            onBlur={() => setFocused(false)}
            className="flex-1 px-4 py-3.5 text-main dark:text-white font-inter text-lg font-semibold focus:outline-none text-end bg-transparent min-w-0"
            placeholder="0"
          />
        </motion.div>
      </div>

      {/* Swap button — 3D flip */}
      <div className="relative flex justify-center py-1 z-10">
        <motion.button
          key={swapKey}
          animate={{ rotate: swapKey * 180 }}
          whileHover={{ scale: 1.2 }}
          whileTap={{ scale: 0.85 }}
          transition={springBouncy}
          onClick={handleSwap}
          className="w-11 h-11 bg-gradient-to-br from-highlight/20 to-highlight/10 hover:from-highlight/30 hover:to-highlight/20 border border-highlight/30 rounded-full flex items-center justify-center shadow-lg shadow-highlight/10"
          aria-label="Swap currencies"
        >
          <HiSwitchHorizontal className="text-highlight text-lg rotate-90" />
        </motion.button>
      </div>

      {/* Result card */}
      <div className="relative mx-6 mb-4 bg-main dark:bg-white/5 rounded-xl p-5 text-center border border-transparent dark:border-white/10 overflow-hidden">
        {/* Subtle inner glow */}
        <div className="absolute inset-0 bg-gradient-to-br from-highlight/5 to-transparent rounded-xl" />
        <div className="relative">
          <p className="text-white/50 dark:text-white/40 text-xs font-cairo mb-2">{resultLabel}</p>
          <div className="flex items-baseline justify-center gap-2" style={{direction: "ltr"}}>
           
            <AnimatePresence mode="wait">
              <motion.span
                key={result()}
                className="text-3xl md:text-4xl font-bold text-highlight font-inter"
              >
                <AnimatedResult value={result()} />
              </motion.span>
            </AnimatePresence>
             <span className="text-white/40 text-sm font-inter">
              {t(`converter.${to}`)} {t(`converter.${to}_label`)}
            </span>
          </div>
        </div>
      </div>

      {/* Exchange rate footer */}
      <div className="relative px-6 pb-5 text-center">
        <motion.span
          key={rateDisplay}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="text-gray-400 dark:text-white/30 text-xs font-inter"
        >
          {t("converter.rate")} {rateDisplay}
        </motion.span>
      </div>
    </motion.div>
  );
}
